/**
 * Mobile Optimization Utilities
 * Provides utilities for enhancing mobile performance and user experience
 */

// Detect mobile devices
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Detect touch capability
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Get viewport dimensions
export const getViewportDimensions = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  };
};

// Optimize images for mobile
export const getOptimizedImageUrl = (url: string, width?: number, quality: number = 80): string => {
  if (!url) return '';
  
  // For mobile, use smaller images
  const mobileWidth = width || (isMobile() ? 400 : 800);
  
  // If using a CDN that supports image optimization, add parameters
  if (url.includes('cloudinary.com') || url.includes('imgix.net')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${mobileWidth}&q=${quality}&f=auto`;
  }
  
  return url;
};

// Lazy load images with intersection observer
export const createLazyImageObserver = (callback: (entries: IntersectionObserverEntry[]) => void) => {
  if (!('IntersectionObserver' in window)) {
    // Fallback for older browsers
    return null;
  }

  return new IntersectionObserver(callback, {
    root: null,
    rootMargin: '50px',
    threshold: 0.1
  });
};

// Optimize scroll performance
export const optimizeScroll = (element: HTMLElement) => {
  if (!element) return;

  // Add passive event listeners for better performance
  const options = { passive: true };
  
  let ticking = false;
  
  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        // Scroll handling logic here
        ticking = false;
      });
      ticking = true;
    }
  };

  element.addEventListener('scroll', handleScroll, options);
  
  return () => {
    element.removeEventListener('scroll', handleScroll);
  };
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/icons/logo.svg',
    '/icons/newstart-logotext.svg'
  ];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Optimize form inputs for mobile
export const optimizeFormInputs = () => {
  const inputs = document.querySelectorAll('input, textarea, select');
  
  inputs.forEach(input => {
    const element = input as HTMLInputElement;
    
    // Prevent zoom on iOS
    if (element.type === 'text' || element.type === 'email' || element.type === 'password') {
      element.style.fontSize = '16px';
    }
    
    // Add touch-friendly styling
    element.style.minHeight = '44px';
    element.style.padding = '12px 16px';
  });
};

// Handle safe area insets for notched devices
export const handleSafeAreaInsets = () => {
  const setCSSVariables = () => {
    const vh = window.innerHeight * 0.01;
    const vw = window.innerWidth * 0.01;
    
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--vw', `${vw}px`);
    
    // Handle safe area insets
    const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)');
    const safeAreaBottom = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)');
    const safeAreaLeft = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-left)');
    const safeAreaRight = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-right)');
    
    document.documentElement.style.setProperty('--safe-area-top', safeAreaTop || '0px');
    document.documentElement.style.setProperty('--safe-area-bottom', safeAreaBottom || '0px');
    document.documentElement.style.setProperty('--safe-area-left', safeAreaLeft || '0px');
    document.documentElement.style.setProperty('--safe-area-right', safeAreaRight || '0px');
  };

  setCSSVariables();
  window.addEventListener('resize', setCSSVariables);
  window.addEventListener('orientationchange', setCSSVariables);
};

// Debounce function for performance
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function for performance
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Initialize mobile optimizations
export const initMobileOptimizations = () => {
  // Handle safe area insets
  handleSafeAreaInsets();
  
  // Preload critical resources
  preloadCriticalResources();
  
  // Optimize form inputs
  optimizeFormInputs();
  
  // Add mobile-specific classes
  if (isMobile()) {
    document.documentElement.classList.add('mobile-device');
  }
  
  if (isTouchDevice()) {
    document.documentElement.classList.add('touch-device');
  }
  
  // Add viewport meta tag if not present
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    document.head.appendChild(viewport);
  }
};

// Performance monitoring for mobile
export const monitorMobilePerformance = () => {
  if (!isMobile()) return;

  // Monitor Core Web Vitals
  if ('web-vital' in window) {
    // This would integrate with web-vitals library
    console.log('Mobile performance monitoring enabled');
  }

  // Monitor memory usage
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    console.log('Memory usage:', {
      used: Math.round(memory.usedJSHeapSize / 1048576) + ' MB',
      total: Math.round(memory.totalJSHeapSize / 1048576) + ' MB',
      limit: Math.round(memory.jsHeapSizeLimit / 1048576) + ' MB'
    });
  }
};

export default {
  isMobile,
  isTouchDevice,
  getViewportDimensions,
  getOptimizedImageUrl,
  createLazyImageObserver,
  optimizeScroll,
  preloadCriticalResources,
  optimizeFormInputs,
  handleSafeAreaInsets,
  debounce,
  throttle,
  initMobileOptimizations,
  monitorMobilePerformance
};
